import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  CircularProgress,
  InputAdornment,
  Backdrop,
  LinearProgress,
  Alert,
  Card,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  attachUserWithMove,
  checkUserExist,
  createUser,
  getMoveService,
  updateMoveService,
} from "../../redux/action/move-service";
import { Phone, Person, Email } from "@mui/icons-material";
import { getLabourAndPacking } from "../../redux/action/labour-and-packing";
import { getJunkRemoval } from "../../redux/action/junk-removal";
import Header from "../header/Header";
import { generateOtp } from "../../redux/action/otp";
import {
  createCustomer,
  createOpportunity,
  saveSmartMovingResponse,
} from "../../redux/action/smart-move-api";
import StepProgress from "../stepsProgress/stepsProgress";

interface UserDetailsProps {
  currentValues: string;
}

const UserDetails: React.FC<UserDetailsProps> = ({ currentValues }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canonicalId, carrierCanonicalId } = useParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  const [moveId, setMoveId] = useState(null);
  const [labourAndPackingId, setLabourAndPackingId] = useState(null);
  const [junkRemovalId, setJunkRemovalId] = useState(null);
  const [currentState, setCurrentState] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showRegisteredAlert, setShowRegisteredAlert] = useState(false);
  const [userId, setUserId] = useState(null);
  const lastStep = 6;
  useEffect(() => {
    setCurrentState(currentValues);
  }, [currentValues]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const navigate = useNavigate();
  const validatePhoneNumber = (phone: string): boolean => {
    return /^\d{10}$/.test(phone);
  };

  const [carrier, setCarrier] = useState<any>(null);
  const [moveData, setMoveData] = useState<any>(null);
  const { businessName } = useParams();
  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .then((res: any) => {});
    };

    if (canonicalId) {
      updateLastStep();
    }
  }, [canonicalId, dispatch, lastStep]);

  useEffect(() => {
    if (canonicalId) {
      if (currentState === "move_service") {
        dispatch<any>(getMoveService(canonicalId))
          .then(unwrapResult)
          .then((res: any) => {
            setMoveId(res.id);
            setMoveData(res);
            setCarrier(res.carrier);
            setPhoneNumber(res.phone_number);
            setLabourAndPackingId(null);
            setJunkRemovalId(null);
          })
          .catch((err: any) => {
            console.error("Failed to fetch move data:", err);
          });
      } else if (currentState === "labour_and_packing") {
        dispatch<any>(getLabourAndPacking(canonicalId))
          .then(unwrapResult)
          .then((res: any) => {
            setLabourAndPackingId(res.id);
            setMoveId(null);
            setJunkRemovalId(null);
            setCarrier(businessName);
          })
          .catch((err: any) => {
            console.error("Failed to fetch move data:", err);
          });
      } else if (currentState === "junk_removal") {
        dispatch<any>(getJunkRemoval(canonicalId))
          .then(unwrapResult)
          .then((res: any) => {
            setLabourAndPackingId(null);
            setJunkRemovalId(res.id);
            setMoveId(null);
          })
          .catch((err: any) => {
            console.error("Failed to fetch move data:", err);
          });
      }
    }
  }, [currentState, canonicalId]);
  const submitLeadToSmartMoving = async () => {
    if (!moveData) return;

    try {
      const providerKey = moveData.carrier?.crm_api_key;
      if (
        providerKey !== null &&
        providerKey !== undefined &&
        providerKey !== ""
      ) {
        if (!providerKey) {
          // setError("No CRM API key found for this carrier");
          return;
        }

        const movingDate = moveData.moving_date
          ? new Date(moveData.moving_date).toISOString().split("T")[0]
          : null;
        const isTestEnvironment =
          process.env.REACT_APP_URL === "http://localhost:3000" ||
          process.env.REACT_APP_URL ===
            "https://staging-booking-tool-react-app-8e4b4d3d9707.herokuapp.com";
        const payload = {
          firstName: isTestEnvironment ? "test" : formData.firstName,
          lastName: isTestEnvironment ? "test" : formData.lastName,
          phoneNumber: formData.phoneNumber,
          email: isTestEnvironment ? "test@gmail.com" : formData.email,
          moveDate: movingDate,
          bedrooms: moveData.number_of_bedrooms,
          moveSize:
            parseFloat(moveData.distance || "0") > 200
              ? moveData.total_cubic_feet
                ? `${moveData.total_cubic_feet} cubic feet`
                : null
              : moveData.pickup_square_footage
              ? `${moveData.pickup_square_footage} sqft`
              : null,
          movingItems:
            parseFloat(moveData.distance || "0") > 200
              ? {
                  inventory: moveData.inventory
                    ? {
                        inventoryData: moveData.inventory.inventory
                          ? JSON.parse(moveData.inventory.inventory)
                          : null,
                      }
                    : null,
                }
              : {
                  BulkyItems: moveData.items
                    ? JSON.parse(moveData.items)
                    : null,
                },
          originAddress: moveData.moving_from,
          destinationAddress: moveData.moving_to,
          moveType: moveData.move_type,
          dwellingType: moveData.dwelling_type,
          floorNumber: moveData.floor_number,
          hasElevator: moveData.isElevator ? "Yes" : "No",
          hasStairs: moveData.isStairs ? "Yes" : "No",
          isPackingRequired: moveData.isPackingRequired ? "Yes" : "No",
          isInsurance: moveData.isInsurance ? "Yes" : "No",
          distance: moveData.distance,
          duration: moveData.duration_time,
          notes: `Move Type: ${moveData.move_type}\nDwelling Type: ${
            moveData.dwelling_type
          }\nFloor Number: ${moveData.floor_number}\nElevator: ${
            moveData.isElevator ? "Yes" : "No"
          }`,
        };
        const endpoint = `https://api.smartmoving.com/api/leads/from-provider/v2?providerKey=${providerKey}`;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!response.ok) {
          const errorData = await response.json();
          // setError(`SmartMoving API error: ${errorData}`);
        }
        const responseData = await response.json();
        const moveServiceData = {
          canonical_id: canonicalId,
          smartmoving_lead_id: responseData.leadId,
        };
        await dispatch<any>(updateMoveService(moveServiceData))
          .then(unwrapResult)
          .then((res: any) => {});
      }
    } catch (error) {
      setError(`Error submitting lead to SmartMoving: ${error}`);
    }
  };
  const handleContinue = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      setError("Please fill all fields");
      return;
    }
    if (!carrierCanonicalId) {
      setError("Carrier ID is missing");
      return;
    }
    try {
      setIsLoading(true);
      setLoadingMessage("Processing your information...");
      setError(null);

      setIsLoading(true);
      setLoadingMessage("Checking user information...");
      setError(null);
      if (moveData) {
        await submitLeadToSmartMoving();
      }
      const existenceCheck = await dispatch<any>(
        checkUserExist({ email: formData.email, carrierCanonicalId })
      ).unwrap();
      if (existenceCheck.exists === true) {
        setLoadingMessage("");
        setIsLoading(false);
        setShowRegisteredAlert(true);

        setTimeout(() => {
          setShowRegisteredAlert(false);
          console.log(existenceCheck, "-----checkkkkk");
          const attachMovePayload = {
            canonicalId: canonicalId,
            userId: existenceCheck.user.canonical_id,
          };
          dispatch<any>(attachUserWithMove(attachMovePayload))
            .then(unwrapResult)
            .then((res: any) => {
              dispatch<any>(
                generateOtp({ email: formData.email, carrierCanonicalId })
              ).unwrap();
              localStorage.setItem("fromUserDetails", "true");
              localStorage.setItem("userEmail", formData.email);
              navigate(
                `/${businessName}/${carrierCanonicalId}/otp-verification`,
                {
                  state: { email: formData.email, moveId: canonicalId },
                }
              );
            });
        }, 4000);
        return;
      } else {
        const moveServiceData = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone_number: phoneNumber,
          moveService_id: moveId || null,
          labourAndPacking_id: labourAndPackingId || null,
          junkRemoval_id: junkRemovalId || null,
          carrierCanonicalId,
        };
        setTimeout(async () => {
          try {
            dispatch<any>(createUser(moveServiceData))
              .then(unwrapResult)
              .then((res: any) => {
                const newUserId = res.id;
                setUserId(newUserId);
                localStorage.setItem("userId", res.id);
                if (res) {
                  dispatch<any>(
                    generateOtp({ email: formData.email, carrierCanonicalId })
                  ).unwrap();
                  localStorage.setItem("fromUserDetails", "true");
                  localStorage.setItem("userEmail", formData.email);
                  localStorage.setItem("userPhoneNumber", phoneNumber);

                  navigate(
                    `/${businessName}/${carrierCanonicalId}/otp-verification`,
                    { state: { email: formData.email, moveId: canonicalId } }
                  );
                }
              });
            if (canonicalId) {
              localStorage.setItem("fromUserDetails", "true");
              // localStorage.setItem("moveServiceCanonicalId", canonicalId);
              setLoadingMessage("Redirecting to login...");
              // navigate("/consumer-login");
            }
          } catch (err: any) {
            setError(err?.message || "Failed to update user details");
          } finally {
            setIsLoading(false);
          }
        }, 2000);
      }
    } catch (err) {
      setIsLoading(false);
      setError("An unexpected error occurred");
    }
  };
  const handleBack = () => {
    navigate(-1);
  };
  return (
    <div>
      <Header />
      <StepProgress currentStep={6} />
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
          onClick={() => {
            setIsLoading(true);
          }}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {showRegisteredAlert && (
        <Box
          sx={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            width: isMobile ? "90%" : "400px",
            animation: "fadeInOut 3s ease-in-out forwards",
            "@keyframes fadeInOut": {
              "0%": { opacity: 0, transform: "translate(-50%, -60%)" },
              "10%": { opacity: 1, transform: "translate(-50%, -50%)" },
              "90%": { opacity: 1, transform: "translate(-50%, -50%)" },
              "100%": { opacity: 0, transform: "translate(-50%, -40%)" },
            },
          }}
        >
          <Alert
            severity="info"
            sx={{
              borderRadius: "12px",
              boxShadow: 3,
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              "& .MuiAlert-icon": {
                color: theme.palette.primary.contrastText,
                fontSize: "2rem",
              },
              "& .MuiAlert-message": {
                fontSize: "1.1rem",
                fontWeight: 500,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Welcome Back!
                </Typography>
                <Typography>
                  You're already registered. Redirecting to login...
                </Typography>
              </Box>
            </Box>
          </Alert>
        </Box>
      )}
      <Container
        sx={{
          width: "100%",
          height: isMobile ? "93vh" : "80vh",
          display: "flex",
          boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 5,
          padding: isMobile ? "20px" : "40px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
          }}
        >
          {/* Main Card */}
          <Card
            elevation={4}
            sx={{
              width: isMobile ? "100%" : "500px",
              padding: isMobile ? "20px" : "35px",
              borderRadius: "16px",
              backgroundColor: "#fff",
              boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Typography
              fontWeight={700}
              fontSize={isMobile ? "18px" : "20px"}
              textAlign="center"
              mb={2}
            >
              Your Personal Information
            </Typography>
            <Typography mb={3} textAlign={"center"} fontSize={14}>
              We need this information to contact you about your moving service
            </Typography>
            <Divider sx={{ background: "#D3D3D3", mb: 3 }} />

            {/* Form Fields */}
            <Box>
              <TextField
                fullWidth
                variant="outlined"
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: theme.palette.primary.main }} />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                variant="outlined"
                label="Phone Number"
                name="phoneNumber"
                type="tel"
                value={phoneNumber}
                disabled
                error={!!error}
                helperText={error}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: theme.palette.primary.main }} />
                      +1
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Card>

          {/* Privacy Message */}
          <Box
            sx={{
              width: isMobile ? "100%" : "500px",
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              backgroundColor: "rgba(173, 216, 230, 0.3)",
              border: "1px solid rgba(173, 216, 230, 0.5)",
              borderRadius: "12px",
              padding: "14px 18px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            }}
          >
            <Typography sx={{ fontSize: "24px" }}>🛡️</Typography>
            <Box>
              <Typography fontWeight={600} fontSize="15px">
                Your privacy is important to us
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#333", lineHeight: 1.5 }}
              >
                We’ll only use your contact information to coordinate your move
                and send important updates. We never share your data with third
                parties.
              </Typography>
            </Box>
          </Box>

          {/* Footer Buttons */}
          <Box
            sx={{
              width: isMobile ? "100%" : "500px",
              display: "flex",
              justifyContent: "space-between",
              mt: 1,
              mb: 3,
            }}
          >
            <Button
              variant="outlined"
              sx={{
                borderRadius: "20px",
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                padding: isMobile ? "8px 20px" : "8px 40px",
                textTransform: "none",
                fontWeight: 600,
              }}
              disabled={isLoading}
              onClick={handleBack}
            >
              Back
            </Button>

            <Button
              variant="contained"
              sx={{
                borderRadius: "20px",
                backgroundColor: theme.palette.primary.main,
                color: "#fff",
                padding: isMobile ? "8px 20px" : "8px 40px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#357ABD" },
                "&:disabled": { opacity: 0.7 },
              }}
              onClick={handleContinue}
              disabled={isLoading}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : null
              }
            >
              {isLoading ? "Processing..." : "Complete Booking"}
            </Button>
          </Box>
        </Box>
      </Container>
    </div>
  );
};

export default UserDetails;
